/** Chat composer: send while a reply streams (queued) or stop it. */
import { useRef, useState } from 'react';
import { useApp } from '../state/AppState';
import { Icon } from './Icon';

export function Composer() {
  const { sendChat, stopChat, isStreaming, paired, queuedChats, removeQueuedChat } = useApp();
  const [text, setText] = useState('');
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);

  const submit = (): void => {
    const value = text;
    if (!value.trim()) return;
    setText('');
    if (textareaRef.current) textareaRef.current.style.height = 'auto';
    sendChat(value);
  };

  const placeholder = paired
    ? isStreaming
      ? '回复中…也可以先说，会自动排队'
      : '发消息…'
    : '尚未配对：先在「设备」页完成配对';
  const canQueue = isStreaming && text.trim().length > 0;

  return (
    <div className="safe-bottom border-t border-white/10 bg-[#080d1a]/95 px-3 pb-2 pt-2">
      {queuedChats.length > 0 ? (
        <div className="mb-1.5 space-y-1">
          {queuedChats.map((item) => (
            <div
              key={item.id}
              className="flex items-center gap-2 rounded-lg border border-white/10 bg-white/[0.04] px-2 py-1 text-[10px] text-slate-400"
            >
              <span className="shrink-0 text-slate-500">排队中</span>
              <span className="min-w-0 flex-1 truncate">{item.text}</span>
              <button
                type="button"
                onClick={() => removeQueuedChat(item.id)}
                aria-label="取消这条排队消息"
                className="shrink-0 rounded px-1 text-slate-500 hover:text-slate-200"
              >
                ×
              </button>
            </div>
          ))}
        </div>
      ) : null}
      <div className="flex items-end gap-2">
        <textarea
          ref={textareaRef}
          rows={1}
          value={text}
          placeholder={placeholder}
          onChange={(event) => {
            setText(event.target.value);
            const element = event.currentTarget;
            element.style.height = 'auto';
            element.style.height = `${Math.min(element.scrollHeight, 128)}px`;
          }}
          onKeyDown={(event) => {
            if (event.key === 'Enter' && !event.shiftKey) {
              event.preventDefault();
              submit();
            }
          }}
          className="max-h-32 min-h-[44px] flex-1 resize-none rounded-2xl border border-white/10 bg-white/5 px-3 py-2.5 text-sm text-slate-100 outline-none placeholder:text-slate-500 focus:border-indigo-400/60"
          aria-label="聊天输入框"
        />
        {canQueue ? (
          <button
            type="button"
            onClick={submit}
            className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-indigo-500 text-white shadow-lg shadow-indigo-950/40"
            aria-label="排队发送"
            title="对方回复完后自动发送"
          >
            <Icon name="send" className="h-5 w-5" />
          </button>
        ) : null}
        <button
          type="button"
          onClick={isStreaming ? stopChat : submit}
          disabled={!isStreaming && text.trim().length === 0}
          className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl text-white shadow-lg transition disabled:opacity-40 ${
            isStreaming ? 'bg-rose-600 shadow-rose-950/40' : 'bg-indigo-500 shadow-indigo-950/40'
          }`}
          aria-label={isStreaming ? '停止生成' : '发送'}
        >
          <Icon name={isStreaming ? 'stop' : 'send'} className="h-5 w-5" />
        </button>
      </div>
      <p className="mt-1 text-[10px] text-slate-600">Enter 发送 · Shift+Enter 换行</p>
    </div>
  );
}
